import {
  ReactiveController,
  ReactiveControllerHost,
  ReactiveElement,
} from 'lit';

import { VisualDesignerContextType } from '@/context';

/**
 * The `VisualDesignerEventController` class implements the `ReactiveController` interface
 * to manage the visual designer context of a host element. It listens for custom events
 * to update the visual designer context and triggers updates in the host element.
 *
 * The class constructor initializes the host and context, and adds the controller to the host.
 * The `_updateEventCallBack` method updates the context with new visual designer data and
 * requests an update in the host element.
 *
 * The `hostConnected` method adds an event listener for the `visual-designer-context-update`
 * event when the host is connected. The `hostDisconnected` method removes the event listener
 * when the host is disconnected.
 */
export class VisualDesignerEventController implements ReactiveController {
  private host: ReactiveControllerHost;

  public context: VisualDesignerContextType;

  constructor(host: ReactiveControllerHost, context: any) {
    (this.host = host).addController(this);
    this.context = context;
  }

  private _updateEventCallBack({
    detail: { visualDesignerData },
  }: CustomEvent) {
    this.context = visualDesignerData;

    /**
     * Needed to trigger the willUpdate mechanism in web component logic
     */
    this.host.requestUpdate();
  }

  hostConnected() {
    const element = this.host as ReactiveElement;
    element.addEventListener('visual-designer-context-update', event => {
      this._updateEventCallBack(event);
    });
  }

  hostDisconnected(): void {
    const element = this.host as ReactiveElement;
    element.removeEventListener('visual-designer-context-update', event => {
      this._updateEventCallBack(event);
    });
  }
}
